import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Content } from "@/src/components/wrappers/Content";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from "@/src/components/ui/navigation-menu";
import { Button } from "@/src/components/ui/button";
import { DateRangePicker } from "@/src/components/ui/date-range-picker";
import { DatePicker } from "@/src/components/ui/date-picker";
import { format } from "date-fns";

import { SummaryTable } from "./tables/SummaryTable";
import { RemainingSummaryTable } from "./tables/RemainingSummaryTable";

type DateRange = { startDate: string; endDate: string };

export function PaymentsSummary() {
  const location = useLocation();

  const [activeTab, setActiveTab] = useState<"summary" | "remaining">("summary");
  const [summaryPickerKey, setSummaryPickerKey] = useState(0);

  const [range, setRange] = useState<DateRange>({ startDate: "", endDate: "" });

  const [bookedBefore, setBookedBefore] = useState<string>("");
  const [bookedAfter, setBookedAfter] = useState<string>("");

  useEffect(() => {
    const qp = new URLSearchParams(location.search);
    setActiveTab(qp.get("tab") === "remaining" ? "remaining" : "summary");
  }, [location.search]);

  const isEmptyRange = !range.startDate && !range.endDate;

  return (
    <Content title="Payments Summary">
      <div className="mb-2">
        <NavigationMenu>
          <NavigationMenuList className="bg-slate-100 border-0">
            <NavigationMenuItem className="!m-0">
              <NavigationMenuLink
                href="/payments-summary?tab=summary"
                active={activeTab === "summary"}
                className="data-[active]:bg-white hover:bg-zinc-50 focus:bg-zinc-50 m-1.5"
              >
                Payments Summary
              </NavigationMenuLink>
            </NavigationMenuItem>
            <NavigationMenuItem className="!m-0">
              <NavigationMenuLink
                href="/payments-summary?tab=remaining"
                active={activeTab === "remaining"}
                className="data-[active]:bg-white hover:bg-zinc-50 focus:bg-zinc-50 m-1.5"
              >
                Remaining Payments Summary
              </NavigationMenuLink>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>
      </div>

      {activeTab === "summary" ? (
        <>
          <div className="mb-2">
            <span className="text-sm text-muted-foreground mr-2">Select date range</span>
            <div className="flex items-center">
              <DateRangePicker
                key={`summary-picker-${summaryPickerKey}`}
                className="max-w-xs w-full bg-white"
                value={isEmptyRange ? (null as any) : (range as any)}
                onChange={(val) => {
                  const next = (val as DateRange) ?? { startDate: "", endDate: "" };
                  setRange(next);
                }}
              />
              <Button
                variant="link"
                onClick={() => {
                  setRange({ startDate: "", endDate: "" });
                  setSummaryPickerKey((k) => k + 1);
                }}
              >
                Clear
              </Button>
            </div>
          </div>
          <SummaryTable from={range.startDate} to={range.endDate} />
        </>
      ) : (
        <>
          <div className="flex flex-col">
            <div className="mb-2">
              <span className="text-sm text-muted-foreground mr-2">Booked before</span>
              <div className="flex items-center">
                <DatePicker
                  disabledDays={{
                    before: new Date(bookedAfter),
                  }}
                  key="remaining-single-picker"
                  className="max-w-xs w-full bg-white"
                  value={bookedBefore ? new Date(bookedBefore) : undefined}
                  onChange={(v) => {
                    if (v) {
                      const formatted = format(v as Date, "yyyy-MM-dd");
                      setBookedBefore(formatted);
                      return v;
                    } else {
                      setBookedBefore("");
                      return "";
                    }
                  }}
                />
              </div>
            </div>
            <div className="mb-2">
              <span className="text-sm text-muted-foreground mr-2">Booked after</span>
              <div className="flex items-center">
                <DatePicker
                  disabledDays={{
                    after: new Date(bookedBefore),
                  }}
                  key="remaining-single-picker"
                  className="max-w-xs w-full bg-white"
                  value={bookedAfter ? new Date(bookedAfter) : undefined}
                  onChange={(v) => {
                    if (v) {
                      const formatted = format(v as Date, "yyyy-MM-dd");
                      setBookedAfter(formatted);
                      return v;
                    } else {
                      setBookedAfter("");
                      return "";
                    }
                  }}
                />
              </div>
            </div>
          </div>
          <RemainingSummaryTable bookedBefore={bookedBefore} bookedAfter={bookedAfter} />
        </>
      )}
    </Content>
  );
}
